import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { FiDownload, FiPlusSquare, FiShare, FiX } from 'react-icons/fi';

const DISMISSED_KEY = 'nido:pwa-install-dismissed';
const AUTH_PATHS = ['/login', '/register', '/verify', '/auth', '/forgot-password', '/reset-password'];

export default function InstallNido() {
  const { pathname } = useLocation();
  const [prompt, setPrompt] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (standalone || sessionStorage.getItem(DISMISSED_KEY)) return undefined;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const onPrompt = (event) => {
      event.preventDefault();
      setPrompt(event);
      window.setTimeout(() => setVisible(true), 5200);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    if (ios) {
      setShowIos(true);
      window.setTimeout(() => setVisible(true), 5200);
    }
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
    window.dispatchEvent(new Event('nido:install-dismissed'));
  };

  const install = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === 'accepted') { setVisible(false); window.dispatchEvent(new Event('nido:install-dismissed')); }
    setPrompt(null);
  };

  useEffect(() => {
    if (visible && (prompt || showIos)) window.dispatchEvent(new Event('nido:install-visible'));
  }, [visible, prompt, showIos]);

  if (AUTH_PATHS.some((path) => pathname.startsWith(path)) || !visible || (!prompt && !showIos)) return null;


  if (showIos && !prompt && showGuide) return <GuideOverlay role="dialog" aria-modal="true" aria-label="Cómo instalar NIDO">
    <GuideBackdrop type="button" onClick={() => setShowGuide(false)} aria-label="Cerrar guía" />
    <GuideSheet>
      <GuideClose type="button" onClick={() => setShowGuide(false)} aria-label="Cerrar"><FiX /></GuideClose>
      <GuideHeader>
        <Icon><img src="/nido-icon-192.png" alt="" /></Icon>
        <div><span>INSTALAR EN IPHONE</span><h2>Tené NIDO como una app.</h2></div>
      </GuideHeader>
      <GuideSteps>
        <GuideStep><StepIcon><FiShare /></StepIcon><div><b>1</b><strong>Tocá Compartir</strong><p>Es el botón con un cuadrado y una flecha hacia arriba, abajo en Safari.</p></div></GuideStep>
        <GuideStep><StepIcon><FiPlusSquare /></StepIcon><div><b>2</b><strong>Elegí “Agregar a inicio”</strong><p>Si no aparece enseguida, deslizá el menú hacia abajo hasta encontrarlo.</p></div></GuideStep>
      </GuideSteps>
      <GuideHint>Después tocá <strong>Agregar</strong> arriba a la derecha. El ícono de NIDO aparecerá en tu pantalla.</GuideHint>
      <GuideDone type="button" onClick={dismiss}>Listo, entendí</GuideDone>
    </GuideSheet>
  </GuideOverlay>;

  return <Card role="dialog" aria-label="Instalar NIDO">
    <Close type="button" onClick={dismiss} aria-label="Cerrar"><FiX /></Close>
    <Icon><img src="/nido-icon-192.png" alt="" /></Icon>
    <Copy><span>NIDO PARA TU TELÉFONO</span><strong>Abrila como una app.</strong>
      {(!showIos || prompt) && <p>Acceso directo y a pantalla completa.</p>}
      {showIos && !prompt && <p>Te mostramos dónde tocar. Son solo dos pasos.</p>}
    </Copy>
    {prompt && <Install type="button" onClick={install}><FiDownload /> Instalar NIDO</Install>}
    {showIos && !prompt && <Install type="button" onClick={() => setShowGuide(true)}>Ver cómo instalar</Install>}
  </Card>;
}

const Card=styled.aside`
  position:fixed;z-index:2200;left:18px;right:18px;bottom:18px;display:none;grid-template-columns:auto 1fr auto;align-items:center;gap:16px;
  max-width:560px;margin:auto;padding:14px 16px;color:#fff;background:rgba(31,54,60,.97);border:1px solid rgba(255,255,255,.13);
  border-radius:20px;box-shadow:0 25px 70px rgba(18,34,38,.3);backdrop-filter:blur(22px) saturate(125%);overflow:hidden;
  animation:nidoInstallIn .55s cubic-bezier(.2,.8,.2,1) both;
  @keyframes nidoInstallIn{from{opacity:0;transform:translateY(24px) scale(.98)}to{opacity:1;transform:none}}
  @media(max-width:900px){display:grid}@media(max-width:520px){grid-template-columns:56px minmax(0,1fr);gap:13px;padding:17px 15px 15px;bottom:10px;left:10px;right:10px;border-radius:22px}
`;
const Close=styled.button`position:absolute;top:3px;right:3px;display:grid;width:25px;height:25px;place-items:center;padding:0;color:rgba(255,255,255,.55);background:transparent;border:0;border-radius:50%;font-size:13px;cursor:pointer;`;
const Icon=styled.div`display:grid;width:52px;height:52px;place-items:center;overflow:hidden;background:#fff;border-radius:14px;box-shadow:0 8px 22px rgba(0,0,0,.18);img{width:100%;height:100%;object-fit:cover}@media(max-width:520px){width:56px;height:56px;border-radius:15px}`;
const Copy=styled.div`min-width:0;padding-right:8px;span,strong,p{display:block}span{margin-bottom:3px;color:#e39a7b;font-size:12px;font-weight:850;letter-spacing:.13em}strong{font-size:18px;line-height:1.15;letter-spacing:-.02em}p{margin:5px 0 0;color:rgba(255,255,255,.72);font-size:13px;line-height:1.4}@media(max-width:430px){span{font-size:12px}strong{font-size:17px}p{font-size:12px}}`;
const Install=styled.button`display:flex;min-height:46px;align-items:center;justify-content:center;gap:7px;padding:0 17px;color:#fff;background:#c96f4c;border:0;border-radius:999px;box-shadow:0 8px 22px rgba(0,0,0,.15);font-size:14px;font-weight:800;white-space:nowrap;cursor:pointer;transition:.2s;&:active{transform:scale(.98)}@media(max-width:520px){grid-column:1/-1;width:100%;min-height:52px;margin-top:2px;font-size:16px}`;

const GuideOverlay=styled.div`position:fixed;z-index:2300;inset:0;display:flex;align-items:flex-end;justify-content:center;padding:12px;color:#29474f;`;
const GuideBackdrop=styled.button`position:absolute;inset:0;width:100%;height:100%;padding:0;border:0;background:rgba(20,37,42,.58);backdrop-filter:blur(4px);`;
const GuideSheet=styled.section`position:relative;z-index:1;width:min(560px,100%);padding:24px 20px calc(20px + env(safe-area-inset-bottom));background:#fff;border-radius:28px 28px 22px 22px;box-shadow:0 -24px 80px rgba(18,34,38,.3);animation:nidoGuideIn .35s ease both;@keyframes nidoGuideIn{from{opacity:0;transform:translateY(35px)}to{opacity:1;transform:none}}`;
const GuideClose=styled.button`position:absolute;top:17px;right:17px;display:grid;width:40px;height:40px;place-items:center;padding:0;border:0;border-radius:50%;background:#eef1ee;color:#29474f;font-size:20px;`;
const GuideHeader=styled.header`display:flex;align-items:center;gap:15px;padding-right:42px;margin-bottom:22px;>div:last-child{min-width:0}>div>span{color:#b85f40;font-size:12px;font-weight:850;letter-spacing:.13em}h2{margin:5px 0 0;font-size:clamp(24px,7vw,32px);line-height:1.05;letter-spacing:-.035em}`;
const GuideSteps=styled.div`display:grid;gap:11px;`;
const GuideStep=styled.div`display:grid;grid-template-columns:54px minmax(0,1fr);gap:14px;align-items:center;padding:15px;background:#f6f4ee;border:1px solid #e4e5df;border-radius:18px;b{display:inline-grid;width:23px;height:23px;margin-right:8px;place-items:center;color:#fff;background:#c96f4c;border-radius:50%;font-size:12px}strong{font-size:17px}p{margin:6px 0 0;color:#657679;font-size:14px;line-height:1.45}`;
const StepIcon=styled.span`display:grid;width:54px;height:54px;place-items:center;color:#c96f4c;background:#fff;border-radius:16px;box-shadow:0 8px 20px rgba(41,71,79,.08);font-size:25px;`;
const GuideHint=styled.p`margin:15px 2px;color:#53686c;font-size:14px;line-height:1.5;`;
const GuideDone=styled.button`width:100%;min-height:54px;border:0;border-radius:999px;color:#fff;background:#29474f;font-size:16px;font-weight:800;`;
